import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../Components/nvr/ui/dialog";

export interface SummaryCardLocation {
  name: string;
  value: number;
}

export interface StudentDistributionSummaryCardData {
  title: string;
  value: string;
  subtitle: string;
  subtitleColor: string;
  iconType: string;
  iconColor: string;
  peakTimestamp?: string;
  locations?: SummaryCardLocation[];
}

interface StudentDistributionSummaryCardProps {
  data: StudentDistributionSummaryCardData;
  isDarkMode?: boolean;
}

const ICON_STYLES: Record<string, string> = {
  orange: "bg-amber-500/20 text-amber-400 dark:bg-amber-500/20 dark:text-amber-400",
  blue: "bg-blue-500/20 text-blue-400 dark:bg-blue-500/20 dark:text-blue-400",
  purple: "bg-violet-500/20 text-violet-400 dark:bg-violet-500/20 dark:text-violet-400",
  green: "bg-emerald-500/20 text-emerald-400 dark:bg-emerald-500/20 dark:text-emerald-400",
};

const getIconStyle = (iconColor: string): string => {
  if (iconColor.includes("orange") || iconColor.includes("amber")) return ICON_STYLES.orange;
  if (iconColor.includes("blue")) return ICON_STYLES.blue;
  if (iconColor.includes("purple") || iconColor.includes("violet")) return ICON_STYLES.purple;
  if (iconColor.includes("green") || iconColor.includes("emerald")) return ICON_STYLES.green;
  return ICON_STYLES.blue;
};

const getIconComponent = (iconType: string, iconColor: string) => {
  const style = getIconStyle(iconColor);
  const iconMap: Record<string, React.ReactNode> = {
    presence: (
      <div className={`p-2.5 rounded-xl shrink-0 ${style}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
    ),
    peak: (
      <div className={`p-2.5 rounded-xl shrink-0 ${style}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      </div>
    ),
    avgDwell: (
      <div className={`p-2.5 rounded-xl shrink-0 ${style}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
    peakDwell: (
      <div className={`p-2.5 rounded-xl shrink-0 ${style}`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    ),
  };
  return iconMap[iconType] ?? null;
};

const CARD_GRADIENTS: Record<string, { dark: string; light: string; accent: string }> = {
  orange: {
    dark: "bg-gradient-to-br from-amber-500/40 via-amber-400/30 to-amber-500/20 border-amber-500/50",
    light: "bg-gradient-to-br from-amber-50 via-amber-100/50 to-amber-50 border-amber-200",
    accent: "bg-amber-500",
  },
  blue: {
    dark: "bg-gradient-to-br from-sky-500/40 via-blue-300/30 to-blue-400/20 border-blue-500/50",
    light: "bg-gradient-to-br from-blue-50 via-blue-100/50 to-blue-50 border-blue-200",
    accent: "bg-blue-500",
  },
  purple: {
    dark: "bg-gradient-to-br from-violet-500/40 via-violet-400/30 to-violet-500/20 border-violet-500/50",
    light: "bg-gradient-to-br from-violet-50 via-violet-100/50 to-violet-50 border-violet-200",
    accent: "bg-violet-500",
  },
  green: {
    dark: "bg-gradient-to-br from-emerald-500/40 via-emerald-400/30 to-emerald-500/20 border-emerald-500/50",
    light: "bg-gradient-to-br from-emerald-50 via-emerald-100/50 to-emerald-50 border-emerald-200",
    accent: "bg-emerald-500",
  },
};

const getCardGradient = (iconColor: string, isDark: boolean) => {
  const key =
    iconColor.includes("orange") || iconColor.includes("amber")
      ? "orange"
      : iconColor.includes("blue")
      ? "blue"
      : iconColor.includes("purple") || iconColor.includes("violet")
      ? "purple"
      : "green";
  const g = CARD_GRADIENTS[key] ?? CARD_GRADIENTS.blue;
  return isDark ? g.dark : g.light;
};

const getAccentColor = (iconColor: string) => {
  const key =
    iconColor.includes("orange") || iconColor.includes("amber")
      ? "orange"
      : iconColor.includes("blue")
      ? "blue"
      : iconColor.includes("purple") || iconColor.includes("violet")
      ? "purple"
      : "green";
  return CARD_GRADIENTS[key]?.accent ?? "bg-blue-500";
};

const getPillLabel = (title: string): string | null => {
  if (title === "Peak Today" || title === "Peak Dwell Time") return "Today";
  if (title === "Current Presence" || title === "Avg Dwell Time") return "Live";
  return null;
};

export const StudentDistributionSummaryCard: React.FC<StudentDistributionSummaryCardProps> = ({
  data,
  isDarkMode = false,
}) => {
  const [locationsModalOpen, setLocationsModalOpen] = useState(false);
  const gradient = getCardGradient(data.iconColor, isDarkMode);
  const accent = getAccentColor(data.iconColor);
  const pill = getPillLabel(data.title);
  const hasLocations = data.locations && data.locations.length > 0;
  const canShowLocationsSection =
    Boolean(hasLocations) && (data.title === "Current Presence" || data.title === "Avg Dwell Time");
  const isDwell = data.title.includes("Dwell");
  const formatLocValue = (val: number) =>
    isDwell ? `${parseFloat((val / 60).toFixed(2))} min` : val;

  return (
    <div
      className={`group relative overflow-hidden rounded-xl p-2.5 sm:p-3 border shadow-lg transition-all duration-300 hover:shadow-xl ${gradient} ${
        isDarkMode ? "text-white" : "text-gray-900"
      }`}
    >
      <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
        <div className="absolute top-0 right-0 w-10 h-10 rounded-full bg-current" style={{ opacity: 0.3 }} />
        <div className="absolute bottom-0 left-0 w-10 h-10 rounded-full bg-current" style={{ opacity: 0.3 }} />
      </div>
      <div className="relative h-full flex flex-col justify-between min-h-[6rem] sm:min-h-[6.5rem]">
        <div className="flex items-center justify-between">
          <div className="shrink-0">{getIconComponent(data.iconType, data.iconColor)}</div>
          {pill && (
            <span
              className={`text-[11px] sm:text-xs font-medium px-2 py-1 rounded-full ${
                isDarkMode ? "bg-white/20 text-white" : "bg-black/10 text-gray-700"
              }`}
            >
              {pill}
            </span>
          )}
        </div>
        <div>
          <h3 className={`text-sm sm:text-base font-bold mb-1 leading-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {data.title}
          </h3>
          <p className={`text-xl sm:text-2xl font-bold tracking-tight break-words ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {data.value}
          </p>
          {data.title === "Peak Today" && data.peakTimestamp ? (
            <p className={`text-xs sm:text-sm mt-0.5 font-medium ${data.subtitleColor}`}>
              at {new Date(data.peakTimestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true })}
            </p>
          ) : (
            <p className={`text-xs sm:text-sm mt-0.5 ${data.subtitleColor}`}>{data.subtitle}</p>
          )}
          {canShowLocationsSection ? (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={() => setLocationsModalOpen(true)}
                className={`text-[11px] sm:text-xs font-medium underline underline-offset-2 hover:opacity-80 transition-opacity ${data.subtitleColor}`}
              >
                Show locations ({data.locations!.length})
              </button>
              <Dialog open={locationsModalOpen} onOpenChange={setLocationsModalOpen}>
                <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-gray-900 dark:text-white">
                      {data.title} — by location
                    </DialogTitle>
                  </DialogHeader>
                  <div className="mt-2 max-h-64 overflow-y-auto">
                    <ul className="space-y-2">
                      {data.locations!.map((loc) => (
                        <li
                          key={loc.name}
                          className="flex justify-between items-center gap-3 py-1.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300"
                        >
                          <span className="truncate">{loc.name}</span>
                          <span className="font-medium">{formatLocValue(loc.value)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          ) : null}
        </div>
        <div className={`w-8 h-0.5 rounded-full ${accent} mt-2`} />
      </div>
    </div>
  );
};

export default StudentDistributionSummaryCard;
